import { useMemo } from 'react';
import { Cell, Pie, PieChart, Sector } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { formatVNDShort } from '@/lib/format';
import type { ReportMonitor } from '@/lib/api';

export type BrandDonutMetric = 'revenue' | 'units';

export interface ReportBrandDonutProps {
  /** Cross-brand monitor list — typically `snapshot.monitors` from an all-brand report */
  monitors: ReportMonitor[];
  /** 'revenue' (default) → sales_revenue; 'units' → sales_units */
  metric?: BrandDonutMetric;
  /** Unique id prefix so multiple donuts on same page don't clash on gradient ids */
  idPrefix?: string;
}

interface BrandShareEntry {
  brand: string;
  value: number;
  share: number;
  skuCount: number;
  /** Resolved CSS color reference, e.g. 'hsl(var(--chart-1))' */
  baseColor: string;
  gradientId: string;
}

/**
 * Group monitors by brand, sum the chosen metric, and assign chart-N tokens
 * (mono-blue palette) ordered by value descending — biggest brand = darkest shade.
 *
 * Limited to 7 brands: extra ones share `--chart-7`. GearVN currently has 7,
 * so this is fine; revisit when a brand catalog expansion happens.
 */
function computeBrandShare(monitors: ReportMonitor[], metric: BrandDonutMetric, idPrefix: string): BrandShareEntry[] {
  const totals = new Map<string, { value: number; skuCount: number }>();
  for (const m of monitors) {
    const value = metric === 'revenue' ? m.sales_revenue : m.sales_units;
    const cur = totals.get(m.brand) ?? { value: 0, skuCount: 0 };
    totals.set(m.brand, { value: cur.value + value, skuCount: cur.skuCount + 1 });
  }
  const totalForShare = Array.from(totals.values()).reduce((s, t) => s + t.value, 0);
  const sorted = Array.from(totals.entries())
    .map(([brand, t]) => ({ brand, value: t.value, skuCount: t.skuCount }))
    .filter((e) => e.value > 0)
    .sort((a, b) => b.value - a.value);

  return sorted.map((e, i) => {
    const tokenIdx = Math.min(i + 1, 7); // chart-1..chart-7
    return {
      brand: e.brand,
      value: e.value,
      share: totalForShare > 0 ? (e.value / totalForShare) * 100 : 0,
      skuCount: e.skuCount,
      baseColor: `hsl(var(--chart-${tokenIdx}))`,
      gradientId: `${idPrefix}-${metric}-${e.brand.replace(/[^a-zA-Z0-9]/g, '_')}`,
    };
  });
}

export function ReportBrandDonut({ monitors, metric = 'revenue', idPrefix = 'brand-donut' }: ReportBrandDonutProps) {
  const data = useMemo(() => computeBrandShare(monitors, metric, idPrefix), [monitors, metric, idPrefix]);
  const total = useMemo(() => data.reduce((sum, d) => sum + d.value, 0), [data]);

  const config = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = {};
    for (const d of data) {
      cfg[d.brand] = { label: d.brand, color: d.baseColor };
    }
    return cfg;
  }, [data]);

  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        Chưa có dữ liệu để tính tỷ trọng theo hãng.
      </div>
    );
  }

  const isRevenue = metric === 'revenue';
  const formatValue = (v: number) => (isRevenue ? formatVNDShort(v) : v.toLocaleString('vi-VN'));
  const totalLabel = isRevenue
    ? `Tổng doanh thu · ${formatVNDShort(total)}`
    : `Tổng số lượng · ${total.toLocaleString('vi-VN')}`;

  return (
    <div className="flex flex-col items-center gap-3">
      <ChartContainer config={config} className="aspect-square h-[180px] w-[180px]">
        <PieChart>
          <defs>
            {data.map((d) => (
              <linearGradient key={d.gradientId} id={d.gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={d.baseColor} stopOpacity={0.95} />
                <stop offset="100%" stopColor={d.baseColor} stopOpacity={0.55} />
              </linearGradient>
            ))}
          </defs>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(value, name, item) => {
                  const num = typeof value === 'number' ? value : Number(value);
                  const p = item.payload as BrandShareEntry | undefined;
                  return (
                    <div className="flex w-full items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div className="h-2.5 w-2.5 rounded-[2px]" style={{ background: p?.baseColor }} />
                        <span className="text-muted-foreground">{p?.brand ?? (name as string)}</span>
                      </div>
                      <span className="font-medium tabular-nums text-foreground">
                        {formatValue(num)}{' '}
                        <span className="ml-1 text-[10px] text-muted-foreground">
                          ({p?.share?.toFixed(1)}%)
                        </span>
                      </span>
                    </div>
                  );
                }}
              />
            }
          />
          <Pie
            data={data}
            dataKey="value"
            nameKey="brand"
            innerRadius={54}
            outerRadius={80}
            strokeWidth={3}
            stroke="hsl(var(--background))"
            paddingAngle={3}
            cornerRadius={6}
            activeShape={(props: object) => (
              <Sector
                {...(props as Parameters<typeof Sector>[0])}
                outerRadius={(props as { outerRadius: number }).outerRadius + 4}
              />
            )}
          >
            {data.map((d) => (
              <Cell key={d.brand} fill={`url(#${d.gradientId})`} fillOpacity={0.95} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>

      <div className="w-full space-y-1">
        <div className="text-center text-[10px] uppercase tracking-wider text-muted-foreground">
          {totalLabel}
        </div>
        <div className="space-y-1 pt-1">
          {data.map((d) => (
            <div key={d.brand} className="flex items-center gap-2 text-[11px]">
              <div className="h-2 w-2 shrink-0 rounded-[2px]" style={{ background: d.baseColor }} />
              <span className="flex-1 truncate font-medium">{d.brand}</span>
              <span className="text-[10px] tabular-nums text-muted-foreground">
                {d.skuCount} SKU
              </span>
              <span className="tabular-nums text-muted-foreground">{formatValue(d.value)}</span>
              <span className="w-10 text-right tabular-nums font-semibold">{d.share.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
